import {
  APIProvider as GoogleMapsApiProvider,
  Map,
  MapCameraChangedEvent
} from '@vis.gl/react-google-maps';
import cx from 'classnames';
import { useState } from 'react';
import { googleMapsApiKey, googleMapsMapId } from '~/constants/environment';
import { useIsMobile } from '~/context/common/DeviceContext';
import type { GeocodeLocation, VendingMachineLocation } from '~/contracts/vm';
import EmptyErrorBoundary from '~/utilities/ErrorBoundary/EmptyErrorBoundary';
import { geographicCenterOfUnitedStates } from './LocationData';
import LocationMarker from './LocationMarker';
import MapFinderSearchArea from './MapFinderSearchArea';
import MapLegend from './MapLegend';

type OwnProps = {
  currentZip: string;
  defaultLocation?: { lat: number; lng: number };
  hasLegend?: boolean;
  isNationwide?: boolean;
  onSubmit: () => void;
  onZipChange: (value: string) => void;
  setGeoCoords: (coords: GeocodeLocation & { label: string }) => void;
  vendingMachineLocations: VendingMachineLocation[];
};

export default function MapFinder({
  currentZip = '',
  defaultLocation = geographicCenterOfUnitedStates,
  hasLegend = true,
  isNationwide = true,
  onSubmit,
  onZipChange,
  setGeoCoords,
  vendingMachineLocations
}: OwnProps) {
  const isMobile = useIsMobile();

  const [activeLocation, updateActiveLocation] = useState<
    Partial<VendingMachineLocation>
  >({
    label: ''
  });
  const [showMarkerLabels, updateShowMarkerLabels] = useState<boolean>(false);

  const center: google.maps.LatLngLiteral = isNationwide
    ? geographicCenterOfUnitedStates
    : defaultLocation;
  const nationwideZoom = isMobile ? 3.2 : 4.7;
  const zoom = isNationwide ? nationwideZoom : 15;
  const maxZoom = isNationwide ? null : zoom + 4;
  const minZoom = isNationwide ? null : zoom - 4;

  const handleZoomChange = (event: MapCameraChangedEvent) => {
    const currentZoom = event.map.getZoom() ?? 0;
    updateShowMarkerLabels(currentZoom > 5);
  };

  return (
    <EmptyErrorBoundary location="Vending Machine Google Map">
      <GoogleMapsApiProvider apiKey={googleMapsApiKey}>
        <Map
          className={cx(
            'relative w-full u:h-dvh',
            !isNationwide && 'h-[50vw] max-h-[840px] lg:h-[100vw]'
          )}
          defaultCenter={center}
          defaultZoom={zoom}
          fullscreenControl={false}
          mapId={googleMapsMapId}
          mapTypeControl={false}
          maxZoom={maxZoom}
          minZoom={minZoom}
          streetViewControl={false}
          zoomControl
          onZoomChanged={handleZoomChange}
        >
          {vendingMachineLocations.map((location, i) => (
            <LocationMarker
              key={`${location.label}-${i}`}
              location={location}
              showMarkerLabels={showMarkerLabels}
              setGeoCoords={setGeoCoords}
              updateActiveLocation={updateActiveLocation}
            />
          ))}
          {hasLegend && (
            <>
              <MapFinderSearchArea
                onChange={onZipChange}
                onSubmit={onSubmit}
                searchTerm={currentZip}
              />
              <MapLegend
                activeLocation={activeLocation}
                vendingMachineLocations={vendingMachineLocations}
                setGeoCoords={setGeoCoords}
                updateActiveLocation={updateActiveLocation}
              />
            </>
          )}
        </Map>
      </GoogleMapsApiProvider>
    </EmptyErrorBoundary>
  );
}
